/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18next, { TFunction } from 'i18next';
import config from 'src/config';
import i18n from 'src/i18n';
import {
  LogState,
  LogTypeEnum,
  QuarantineLogTypeEnum,
  SearchItem,
  TimeRangeInterface,
  SecurityFilterName,
} from '@Types/log';

import { urlMask } from '../../utils/common';
import {
  constructSecurityRiskName,
  riskAiName,
} from '../Quarantine/RiskAI/utils';

dayjs.extend(utc);

const l10nMap = () => {
  let instance: {
    l10nMap: { [key: string]: any };
    language: string;
    quarantineL10nObj: { [key: string]: any };
    dlpL10nObj: { [key: string]: any };
  } = {
    l10nMap: {},
    language: '',
    quarantineL10nObj: {},
    dlpL10nObj: {},
  };
  function createL10nInstance() {
    const language = i18n.language;
    const l10nMap = i18n.store.data[language];
    const quarantineL10nObj = nestObject(l10nMap['quarantine'])['quarantine'];
    const dlpL10nObj = nestObject(l10nMap['dlp'])['dlp'];
    return {
      l10nMap,
      language,
      quarantineL10nObj,
      dlpL10nObj,
    };
  }
  return {
    getL10nMap: () => {
      console.log('construct getL10nMap');
      if (instance.language !== i18n.language) {
        instance = createL10nInstance();
      }
      return instance.l10nMap;
    },
    getQuarantineL10nObj: () => {
      if (instance.language !== i18n.language) {
        instance = createL10nInstance();
      }
      return instance.quarantineL10nObj;
    },
    getDlpL10nObj: () => {
      if (instance.language !== i18n.language) {
        instance = createL10nInstance();
      }
      return instance.dlpL10nObj;
    },
  };
};

export const { getL10nMap, getDlpL10nObj, getQuarantineL10nObj } = l10nMap();

export const isLogType = (type: string): type is LogTypeEnum => {
  return (Object.values(LogTypeEnum) as string[]).includes(type);
};

export enum LogTimeUnit {
  Last24h = '24h',
  Last7d = '1w',
  Last30d = '1M',
  All = 'all',
  Custom = 'custom',
}

export const getMaxLogQueryRangeDays = (
  logType: LogTypeEnum | QuarantineLogTypeEnum,
  defaultDays = 60
): number => {
  if (
    Object.values(QuarantineLogTypeEnum).includes(
      logType as QuarantineLogTypeEnum
    )
  ) {
    return 90; // [Operation] Quarantine logs have a different default
  }
  if (
    [LogTypeEnum.URLTRACKING, LogTypeEnum.QUARANTINE].includes(
      logType as LogTypeEnum
    )
  ) {
    return 90; // 90 days for URL tracking and log query quarantine logs
  }
  if (LogTypeEnum.INLINETRACKING === logType) {
    return 60; // 60 days for inline tracking logs
  }
  return defaultDays;
};

export const getTimeRange = (
  timeUnit: Exclude<LogTimeUnit, LogTimeUnit.Custom>,
  logType: LogTypeEnum | QuarantineLogTypeEnum
): TimeRangeInterface => {
  const allBasedDays = getMaxLogQueryRangeDays(logType);
  const offset =
    localStorage.getItem('casUtcOffset') || config.ui.timeZone.defaultOffset;
  const days = {
    '24h': 1,
    '1w': 7,
    '1M': 30,
    all: allBasedDays,
  }[timeUnit];
  const endTime = dayjs().utcOffset(offset);
  let startTime = endTime.startOf('day').subtract(days, 'day');

  if ([LogTimeUnit.Last24h].includes(timeUnit)) {
    startTime = endTime.subtract(days, 'day');
  }

  return {
    predefined: timeUnit,
    start: startTime.toISOString(),
    end: endTime.toISOString(),
    timezoneOffset: offset,
  };
};

export const getInitState = (): LogState => {
  const casLogType = getInitLogType();

  return {
    ...config.ui.logDefaultValue.logState,
    type: casLogType,
    timeRange: getTimeRange(LogTimeUnit.Last24h, casLogType),
    page: {
      start: 1,
      size: casLogType === LogTypeEnum.URLTRACKING ? 20 : 100,
    },
  };
};

export const getInitLogType = (): LogTypeEnum => {
  let casLogType = localStorage.getItem('casLogType') as LogTypeEnum;
  if (!(casLogType && isLogType(casLogType))) {
    casLogType = LogTypeEnum.DETECTION;
  }
  return casLogType;
};

export const convertFlattenJsonToNestedJson = (input: Object): Object => {
  function convertToNestedObject(key: string, value: any) {
    const keys = key.split('.');
    const result: { [key: string]: any } = {};
    let obj = result;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      obj[k] = {};
      obj = obj[k];
    }
    obj[keys[keys.length - 1]] = value;
    return result;
  }

  const output = {};
  for (const [key, value] of Object.entries(input)) {
    Object.assign(output, convertToNestedObject(key, value));
  }

  console.log(output);
  return output;
};

export function nestObject(obj: any) {
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    const keys = key.split('.');
    keys.reduce((acc, curVal, idx) => {
      if (!acc[curVal]) {
        acc[curVal] = {};
      }
      if (idx === keys.length - 1) {
        acc[curVal] = obj[key];
      }
      return acc[curVal];
    }, result);
  }
  return result;
}

export const unwrapSearchFieldFuncMap = {
  suspiciousUrl: (
    value: string,
    type: 'searchCondition' | 'autoComplete' = 'searchCondition'
  ): string[] => {
    const urlCategoryMap = getQuarantineL10nObj()['urlCategory'];
    const values: string[] = [];
    if (typeof urlCategoryMap === 'object') {
      Object.keys(urlCategoryMap).forEach((key) => {
        if (type === 'searchCondition') {
          if (urlCategoryMap[key] === value) {
            values.push(key);
          }
        } else if (type === 'autoComplete') {
          if (urlCategoryMap[key].toLowerCase().includes(value.toLowerCase())) {
            values.push(urlCategoryMap[key]);
          }
        }
      });
    }
    return values;
  },
  dlpTemplate: (
    value: string,
    type: 'searchCondition' | 'autoComplete' = 'searchCondition'
  ): string[] => {
    const dlpEntityL10nMap = getDlpL10nObj()['ENTITY'];
    const values: string[] = [];
    if (typeof dlpEntityL10nMap === 'object') {
      Object.keys(dlpEntityL10nMap).forEach((key) => {
        if (type === 'searchCondition') {
          if (dlpEntityL10nMap[key]['DISPLAYNAME'] === value) {
            values.push(key);
          }
        } else if (type === 'autoComplete') {
          if (
            dlpEntityL10nMap[key]['DISPLAYNAME']
              .toLowerCase()
              .includes(value.toLowerCase())
          ) {
            values.push(dlpEntityL10nMap[key]['DISPLAYNAME']);
          }
        }
      });
    }
    return values;
  },
};

type DashboardTimeRange = '24h' | '1w' | '1M' | string;

export function getCurrentOrgId() {
  const casCurSelectedOrgId =
    localStorage.getItem('casCurSelectedOrgId') || 'all';
  return casCurSelectedOrgId;
}
export function generateRedirectUrlToLog(
  searchFields: {
    field: string;
    values: string[];
    type: 'terms' | 'term' | 'queryString' | 'mustNot' | 'match';
    subType?: string;
  }[],
  dashboardTimeRange: DashboardTimeRange,
  type: LogTypeEnum,
  orgId?: string,
  criteria?: {
    field: string;
    value: string;
    label: string;
  }[]
) {
  const targetOrgId = orgId || getCurrentOrgId();
  const facets = searchFields.map((item) => {
    const newItem: SearchItem = {
      field: item.field,
      values: item.values,
      type: item.type,
    };
    if (item.type === 'mustNot') {
      newItem['subType'] = item.subType;
    }
    return newItem;
  });
  if (targetOrgId !== 'all') {
    facets.push({
      field: 'companyGuid',
      values: [targetOrgId],
      type: 'terms',
    });
  }
  const timeRange: { predefined: DashboardTimeRange } = {
    predefined: dashboardTimeRange,
  };
  let url = `type=${type}&facets=${encodeURIComponent(
    JSON.stringify(facets)
  )}&timeRange=${JSON.stringify(timeRange)}`;

  if (criteria && criteria.length > 0) {
    url += `&criteria=${encodeURIComponent(JSON.stringify(criteria))}`;
  }

  return `${url}`;
}

export const wrapSearchFieldFuncMap = {
  suspiciousUrl: (key: string, l10nMap: { [key: string]: any }): string => {
    const urlCategoryMap = l10nMap['urlCategory'];
    let result = key;
    Object.keys(urlCategoryMap).forEach((l10nKey) => {
      if (l10nKey === key) {
        result = urlCategoryMap[key];
      }
    });
    return result;
  },
};

export function removeUrlState() {
  if (window.top) {
    const url = new URL(window.top.location.href);
    const mainUrl = url.href.split('?')[0];
    window.top.history.replaceState({}, '', mainUrl);
  }
}

export function getApiIntegrationSecurityRiskName(
  logItem: any,
  t: TFunction
): string {
  let prefix = '';
  let suffix = '';
  const reason = logItem?.blockReason?.[0] || '';
  if (!reason) return '';

  switch (reason) {
    case 'Blocked Sender':
      suffix = logItem['msgFrom'] || '';
      prefix = t('log.apiLog.remediationApi.blockedSender');
      break;
    case 'Blocked URL':
      suffix = logItem['suspiciousUrl'] || '';
      prefix = t('log.apiLog.remediationApi.blockedUrl');
      break;
    // Blocked HASH is the old version, we should keep support it
    case 'Blocked HASH':
    case 'Blocked SHA-1 Value':
      suffix = logItem['fileSha1'] || '';
      prefix = t('log.apiLog.remediationApi.blockedSha1');
      break;
    case 'Blocked SHA-256 Value':
      suffix = logItem['fileSha256'] || '';
      prefix = t('log.apiLog.remediationApi.blockedSha256');
      break;
  }

  return suffix ? prefix + ': ' + suffix : prefix;
}

const msgFromFunc = (row: any): string => {
  if (row['msgFrom'] && row['msgFrom'] instanceof Array) {
    return row['msgFrom'].join('; ');
  } else {
    return row['msgFrom'] || '';
  }
};
const msgToFunc = (row: any): string => {
  if (row['msgTo'] && row['msgTo'] instanceof Array) {
    return row['msgTo'].join('; ');
  } else {
    return row['msgTo'] || '';
  }
};
const blockReasonFunc = (row: any): string => {
  const { t } = i18next;
  const _blockReason: string[] = [];
  if (row['blockReason'].length > 0) {
    row['blockReason'].forEach(function (item: string) {
      _blockReason.push(urlMask(item, true));
    });
  }
  row['blockReason'] = _blockReason;

  if (row.securityFilterName === 'Malware Scanning') {
    let showString = row['blockReason'].join(';');
    if (showString === 'DisarmMacro') {
      showString = t('log.disarmMacro');
    }
    if (showString === 'DisarmRiskyFile') {
      showString = t('log.disarmRiskyFile');
    }
    return t('quarantine.malware') + ': ' + showString;
  }
  if (riskAiName.includes(row.securityFilterName)) {
    return `${constructSecurityRiskName(
      row.raiMatchedRules?.[0]?.id,
      row.raiMatchedRules?.[0]?.name,
      t
    )}`;
  }

  // securityFilterName in log content, filterType in quarantine content
  // although the key is different, it is actually the same meaning
  if (
    row['securityFilterName'] === SecurityFilterName.WebReputation ||
    row['filterType'] === SecurityFilterName.WebReputation
  ) {
    return row['blockReason'].join(';') + ', ' + suspiciousUrlFunc(row);
  }
  return row['blockReason'].join(';');
};

const suspiciousUrlFunc = (row: any): string => {
  const { t } = i18next;
  const urlCategory = ((row['urlCategory'] || []) as string[]).join(' ').trim();
  if (urlCategory === '') {
    return urlMask(row['suspiciousUrl'] || '');
  }
  const categoryArray = urlCategory.split(/\s+/);
  for (let j = 0; j < categoryArray.length; j++) {
    const i18nKey = `quarantine.urlCategory.${categoryArray[j]}`;
    if (t(i18nKey) !== i18nKey) {
      categoryArray[j] = t(i18nKey);
    }
  }
  return categoryArray.join(' - ') + ': ' + urlMask(row['suspiciousUrl']);
};

const spamCategoryFunc = (row: any): string => {
  const { t } = i18next;
  const res: string[] = [];
  for (const v of row['spamCategory'] as string[]) {
    const i18nKey = `quarantine.spamCategory.${v}`;
    res.push(t(i18nKey) === i18nKey ? v : t(i18nKey));
  }
  return res.join(' - ');
};

const dlpTemplateFunc = (row: any): string => {
  const { t } = i18next;
  if (Array.isArray(row.dlpRule) && row.dlpRule.length > 0) {
    const ret: string[] = [];
    row.dlpRule.forEach((rule: string, index: number) => {
      ret.push(
        t(
          `dlp.ENTITY.${rule}.DISPLAYNAME`,
          row.dlpTemplate[index] || ''
        ) as string
      );
    });
    return ret.join('; ');
  } else {
    return row.dlpTemplate.join('; ');
  }
};

const dlpForensicDataContentFunc = (row: any): string => {
  return row['dlpForensicDataContent'];
};

const fbTrueFileTypeFunc = (row: any): string => {
  const { t } = i18next;
  const trueFileType = row['fbTrueFileType'];
  if (!trueFileType) {
    return row['fileName'] || '';
  }
  const i18nKey = `quarantine.fbTrueFileType.${trueFileType}`;
  const trueFileTypeTitle = t(i18nKey) === i18nKey ? trueFileType : t(i18nKey);
  return trueFileTypeTitle + ': ' + row['fileName'] || '';
};

const tmaseXheadersFunc = (row: any): string => {
  if (row['tmaseXheaders'] && row['tmaseXheaders'] instanceof Array) {
    return row['tmaseXheaders'].join('; ');
  } else {
    return row['tmaseXheaders'] || '';
  }
};

const msgMimeSenderFunc = (row: any): string => {
  if (row['msgMimeSender'] && row['msgMimeSender'] instanceof Array) {
    return row['msgMimeSender'].join('; ');
  } else {
    return row['msgMimeSender'] || '';
  }
};

//just for quarantine
const errorCodeFunc = (row: any): string => {
  const { t } = i18next;
  const errorCode = row['errorCode'];
  if (errorCode && errorCode !== '0') {
    const i18nKey = `quarantine.errorCode.${errorCode}`;
    if (t(i18nKey) !== i18nKey) {
      return t(i18nKey);
    }
  }
  return '';
};

//just for quarantine
const mailLocationFunc = (row: any): string => {
  const { t } = i18next;
  const mailLocation = row['mailLocation'];
  return mailLocation
    ? t(`quarantine.mailLocationReplace`, { FOLDER: mailLocation })
    : '';
};

export const wrapLogFieldFuncMap = new Map([
  ['msgFrom', msgFromFunc],
  ['msgTo', msgToFunc],
  ['blockReason', blockReasonFunc],
  ['suspiciousUrl', suspiciousUrlFunc],
  ['spamCategory', spamCategoryFunc],
  ['dlpTemplate', dlpTemplateFunc],
  ['dlpForensicDataContent', dlpForensicDataContentFunc],
  ['fbTrueFileType', fbTrueFileTypeFunc],
  ['tmaseXheaders', tmaseXheadersFunc],
  ['msgMimeSender', msgMimeSenderFunc],
  ['errorCode', errorCodeFunc],
  ['mailLocation', mailLocationFunc],
]);

export const handleVASecurityRiskName = (log: any) => {
  if (log['securityFilterName'] !== 'Virtual Analyzer') return;

  // if no risk , hide the value
  if (
    log['ddaaasRiskLevel'] &&
    !['Low Risk', 'Medium Risk', 'High Risk'].includes(log['ddaaasRiskLevel'])
  ) {
    log['mergedSecurityRiskName'] = '';
  }
};

const TABLE_CONFIG_VERSION = 'V2_';
export const getLogTableConfig = () => {
  return JSON.parse(
    localStorage.getItem(TABLE_CONFIG_VERSION + 'casLogTableConfig') || '{}'
  );
};
export const setLogTableConfig = (config: any) => {
  localStorage.setItem(
    TABLE_CONFIG_VERSION + 'casLogTableConfig',
    JSON.stringify(config)
  );
};
